/** Fired to open the assistant drawer (mounted once, in the header). */
export const OPEN_ASSISTANT_EVENT = "crx:open-assistant";

export interface OpenAssistantDetail {
    /** A question to put in the box. */
    question?: string;
    /** Ask it straight away (asked from search) instead of leaving it in the box. */
    send?: boolean;
    /** A file to attach ("Fill with AI" on a new request or mission): read and acted on like Attach. */
    file?: File | null;
}

/**
 * Open the assistant from anywhere (e.g. the "Ask HR" buttons, the search box).
 * @param question - Optional question.
 * @param options - `send`: ask it at once rather than leave it in the box.
 */
export const openAssistant = (question?: string, options: { send?: boolean; file?: File | null } = {}): void => {
    window.dispatchEvent(new CustomEvent<OpenAssistantDetail>(OPEN_ASSISTANT_EVENT, { detail: { question, send: options.send, ...(options.file ? { file: options.file } : {}) } }));
};

const QUESTION_START = /^(what['’]s|who['’]s|where['’]s|how['’]s|whats|who|whom|whose|what|which|when|where|why|how|is|are|was|were|do|does|did|can|could|should|would|will|has|have|any|list|show|give|tell|summari[sz]e|compare|explain|find me|من|ما|ماذا|كم|هل|متى|أين|اين|لماذا|كيف|أي|اي)\s/i;

/**
 * Whether typed search text reads like a question for the assistant rather
 * than a record to find: it ends with a question mark, starts with a question
 * word (English or Arabic) and has a few words, or is a longer sentence.
 * "MSN-0142", "Nordwind" or an email stay searches.
 * @param text - What was typed.
 */
export const looksLikeQuestion = (text: string): boolean => {
    const trimmed = text.trim();
    if (!trimmed) return false;
    if (/[?؟]$/.test(trimmed)) return true;
    const words = trimmed.split(/\s+/).length;
    if (QUESTION_START.test(trimmed)) return words >= 3;
    return words >= 6;
};

/** Record pages the assistant knows about ("this client", "this invoice" …). */
export type AskPageKind = "mission" | "request" | "client" | "provider" | "invoice" | "bill";

const ASK_PAGES: Array<[RegExp, AskPageKind]> = [
    [/^\/mission\/(\d+)(?:\/|$)/, "mission"],
    [/^\/mission-request\/(\d+)(?:\/|$)/, "request"],
    [/^\/client\/(\d+)(?:\/|$)/, "client"],
    [/^\/provider\/(\d+)(?:\/|$)/, "provider"],
    [/^\/finance\/invoice\/(\d+)(?:\/|$)/, "invoice"],
    [/^\/finance\/bill\/(\d+)(?:\/|$)/, "bill"],
];

/**
 * The record page the user is on, for the assistant's page context. The
 * server reads the record again as the user, so this is only a hint.
 * @param pathname - The route, e.g. "/client/7".
 * @returns The page kind and record id, or null on any other page.
 */
export const askPageFrom = (pathname: string): { kind: AskPageKind; id: number } | null => {
    for (const [pattern, kind] of ASK_PAGES) {
        const match = pattern.exec(pathname);
        if (match) return { kind, id: Number(match[1]) };
    }
    return null;
};

/** Fired to open a form Ask prepared (mounted once, in the header). */
export const OPEN_DRAFT_EVENT = "crx:open-draft";

/** Open a form empty (a "Do" row in search): no AI, nothing filled, nothing saved. */
export const openForm = (form: { kind: string; title: string; values: Record<string, any>; warnings: string[] }): void => {
    window.dispatchEvent(new CustomEvent(OPEN_DRAFT_EVENT, { detail: { ...form, manual: true } }));
};

/**
 * Open a prepared form for the person to review and save. Nothing is saved
 * until they press Save in that form.
 * @param draft - The draft from an answer.
 */
export const openDraft = (draft: { kind: string; title: string; values: Record<string, any>; warnings: string[] }): void => {
    window.dispatchEvent(new CustomEvent(OPEN_DRAFT_EVENT, { detail: draft }));
};
